import { Component, OnInit, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { WorkItem } from '@rapider/sdk';
import { HomeDataService } from '../services/home-data.service';
import { WorkitemsActions } from '../state/workitems.actions';
import {
  selectWorkitems,
  selectIsLoadingWorkitems,
  selectSelectedWorkitem,
} from '../state/workitems.reducer';

import {
  RapiderPageHeaderComponent,
  PageHeaderPill,
} from '@rapider/components';

import { NzDropDownModule } from 'ng-zorro-antd/dropdown';

@Component({
  selector: 'rapider-workitems-home',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RapiderPageHeaderComponent,
    NzDropDownModule,
  ],
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.css'],
  host: {
    class: 'block w-full h-full min-h-0',
  },
})
export class HomeComponent implements OnInit {
  private homeDataService = inject(HomeDataService);
  private store = inject(Store);

  // Directly select signals from NgRx store for instant UI rendering (SWR)
  public workItems = this.store.selectSignal(selectWorkitems);
  public isLoading = this.store.selectSignal(selectIsLoadingWorkitems);
  public selectedItem = this.store.selectSignal(selectSelectedWorkitem);

  public selectedKindFilter = signal<string>('all');
  public selectedAppFilter = signal<string>('all');

  public headerPills = computed<PageHeaderPill[]>(() => [
    { key: 'all', label: 'All items', count: this.workItems().length },
    { key: 'needsAttention', label: 'Needs attention' },
    { key: 'approval', label: 'Approvals' },
    { key: 'task', label: 'Tasks' },
    { key: 'report', label: 'Reports' },
  ]);

  public headerSelectOptions = [
    { value: 'all', label: '⊞ All applications' },
    { value: 'finance-app', label: 'Finance' },
    { value: 'gym-app', label: 'Gym & Training' },
    { value: 'crm-app', label: 'CRM' },
  ];

  ngOnInit(): void {
    this.loadWorkItems();
  }

  generateMockData(): void {
    const mockItems = [
      {
        source: { type: 'application', typeId: 'finance-app' },
        kind: 'approval',
        title: 'Approve the September supplier invoice',
        bodyMarkdown: '## Review\nThe invoice matches the purchase order.',
        recommendation: {
          text: 'Approve for processing; do not release payment.',
        },
        occurredAt: '2026-09-18T16:35:00Z',
        dedupeKey: 'invoice:2048:approval:revision-3',
        needsAttention: true,
        status: 'pending',
      },
      {
        source: { type: 'application', typeId: 'gym-app' },
        kind: 'task',
        title: 'Update workout schedule',
        bodyMarkdown: 'Update the new gym schedule for the week.',
        occurredAt: '2026-09-19T08:00:00Z',
        dedupeKey: 'gym:schedule:update:42',
        needsAttention: false,
        status: 'completed',
      },
      {
        source: { type: 'application', typeId: 'crm-app' },
        kind: 'report',
        title: 'Weekly Sales Report',
        bodyMarkdown: '## Q3 Update\nSales are up by 15%.',
        occurredAt: '2026-09-17T10:00:00Z',
        dedupeKey: 'crm:report:weekly:q3',
        needsAttention: true,
        status: 'completed',
      },
    ];

    let completed = 0;
    for (const item of mockItems) {
      this.homeDataService['workItemService']
        .create({
          body: item as any,
        })
        .subscribe({
          next: () => {
            completed++;
            if (completed === mockItems.length) {
              this.loadWorkItems();
            }
          },
          error: (err: any) =>
            console.error('Failed to create mock item:', err),
        });
    }
  }

  loadWorkItems(): void {
    this.store.dispatch(
      WorkitemsActions.loadWorkitems({
        filters: {
          kind: this.selectedKindFilter(),
          applicationId: this.selectedAppFilter(),
        },
      }),
    );
  }

  onFilterChange(): void {
    this.loadWorkItems();
  }

  setKindFilter(kind: string): void {
    this.selectedKindFilter.set(kind);
    this.onFilterChange();
  }

  setAppFilter(app: string): void {
    this.selectedAppFilter.set(app);
    this.onFilterChange();
  }

  selectItem(item: WorkItem): void {
    this.store.dispatch(WorkitemsActions.selectWorkitem({ id: item.id }));
    this.store.dispatch(WorkitemsActions.loadWorkitemDetail({ id: item.id }));
  }

  handleAction(action: 'approve' | 'reject'): void {
    const current = this.selectedItem();
    if (!current) return;

    const targetStatus = action === 'reject' ? 'rejected' : 'completed';
    this.homeDataService.updateItemStatus(current.id, targetStatus).subscribe();
  }

  saveToNotebook(): void {
    const host = (window as any).RapiderHomeHost;
    if (host && this.selectedItem()) {
      host.saveNotebook({
        id: `nb-${Date.now()}`,
        title: `Note for ${this.selectedItem()?.title}`,
        folder: 'inbox-notes',
        workItemId: this.selectedItem()?.id,
        text: `Saved from Home: ${this.selectedItem()?.title}`,
      });
    } else {
      console.warn('RapiderHomeHost not available or no item selected');
    }
  }

  getApplicationId(item: WorkItem | null | undefined): string {
    if (!item) return '';
    const src = item.source as any;
    return src?.typeId || '';
  }

  navigateToSource(): void {
    const host = (window as any).RapiderHomeHost;
    const item = this.selectedItem();
    if (host && item) {
      const src = item.source as any;
      if (src?.type === 'notebook') {
        host.navigate({
          destination: 'notebooks',
          spaceKey: src?.typeId,
          noteKey: src?.entityId,
          workItemId: item.id,
          projectId: item.projectId,
        });
        return;
      }
      host.navigate({
        destination: 'application',
        applicationKey: this.getApplicationId(item),
        workItemId: item.id,
        projectId: item.projectId,
      });
    }
  }
}
